import strings from "global/constants/StringConstants";
import {
  convertESTtoUserLocalDateAndTime,
  splitByCamelCase,
} from "helpers/methods";
import {
  RequestAudit,
  RequestParticipant,
  SignRequest,
} from "models/interfaces";
import {
  ViewSignRequestStatusKey,
  viewSignRequestStatusTokens,
} from "./ViewSignRequest.tokens";

/** "Jul 10, 2026 · 2:41 AM" — the one timestamp shape the design uses. */
export const DATE_TIME_FORMAT = "MMM D, YYYY [·] h:mm A";
/** Dates without a time ("Expires Aug 9, 2026"). */
export const DATE_FORMAT = "MMM D, YYYY";

/** `SignRequest.createdOn` and `RequestAudit.createdOn` arrive differently. */
const REQUEST_DATE_INPUT = "MM/DD/YYYY HH:mm:ss";
const AUDIT_DATE_INPUT = "YYYY-MM-DDTHH:mm:s";

export interface StatusVisual {
  key: ViewSignRequestStatusKey;
  label: string;
}

/** A signer/approver is "done" once they have signed / approved. */
const isDone = (status?: string) =>
  status === strings.signed ||
  status === strings.completed ||
  status === strings.approved;

/**
 * Maps the request's status onto the design's chip vocabulary. The design
 * splits the app's single `Pending` status in two — "Pending" while nobody has
 * acted and "In progress" once somebody has — so the signer tally decides which
 * chip a pending request gets. Statuses the design has no chip for (Scheduled,
 * Voided, …) fall back to the neutral treatment but keep their own label so no
 * information is lost.
 */
export const getRequestStatusVisual = (
  request: SignRequest | undefined,
): StatusVisual => {
  const status = request?.status;
  const { signed } = computeSignerProgress(request?.signers);

  switch (status) {
    case strings.completed:
    case strings.signed:
    case strings.approved:
      return { key: "completed", label: viewSignRequestStatusTokens.completed.label };
    case strings.declined:
    case strings.rejected:
      return { key: "declined", label: viewSignRequestStatusTokens.declined.label };
    case strings.pending:
    case strings.inReview:
      return signed > 0
        ? { key: "progress", label: viewSignRequestStatusTokens.progress.label }
        : { key: "pending", label: viewSignRequestStatusTokens.pending.label };
    default:
      return {
        key: "neutral",
        label: status ? splitByCamelCase(status) : viewSignRequestStatusTokens.neutral.label,
      };
  }
};

/** Per-recipient status chip (Signed / Pending / Declined / …). */
export const getSignerStatusVisual = (status?: string): StatusVisual => {
  switch (status) {
    case strings.signed:
    case strings.completed:
    case strings.approved:
      return { key: "completed", label: splitByCamelCase(status) };
    case strings.pending:
    case strings.delay:
      return { key: "pending", label: viewSignRequestStatusTokens.pending.label };
    case strings.declined:
    case strings.rejected:
      return { key: "declined", label: splitByCamelCase(status) };
    case strings.Delegated:
      return { key: "neutral", label: "Delegated" };
    default:
      return {
        key: "neutral",
        label: status ? splitByCamelCase(status) : "",
      };
  }
};

/** Signing progress across the request's signers (CC excluded). Approvers are
 * not signers, so they do not count toward signing progress. */
export const computeSignerProgress = (signers: RequestParticipant[] = []) => {
  const actionable = signers.filter((s) => !s.cc);
  const total = actionable.length;
  const signed = actionable.filter((s) => isDone(s.status)).length;
  const pct = total > 0 ? Math.round((signed / total) * 100) : 0;
  return { signed, total, pct };
};

export interface BannerConfig {
  show: boolean;
  isError: boolean;
  title: string;
  sub: string;
  /** Completed requests offer the signed bundle + completion certificate. */
  showDownload: boolean;
}

const hiddenBanner: BannerConfig = {
  show: false,
  isError: false,
  title: "",
  sub: "",
  showDownload: false,
};

/**
 * Completion / declined / voided / expired strip under the header card.
 * `completedOn` is the already-formatted date of the terminal audit event and
 * is appended to the success copy exactly as the design does.
 */
export const getBannerConfig = (
  status: string | undefined,
  isSigned: boolean,
  completedOn?: string,
): BannerConfig => {
  if (isSigned || status === strings.completed || status === strings.signed) {
    return {
      show: true,
      isError: false,
      title: "All signers have completed this request",
      sub: `The signed documents and completion certificate were emailed to all parties${
        completedOn ? ` on ${completedOn}` : ""
      }.`,
      showDownload: true,
    };
  }

  if (status === strings.declined || status === strings.rejected) {
    return {
      show: true,
      isError: true,
      title: "This request was declined",
      sub: "A signer declined to sign — see their reason on the signer below. Remaining signers are on hold; correct the document and send a new request.",
      showDownload: false,
    };
  }

  if (status === strings.voided) {
    return {
      show: true,
      isError: true,
      title: "This request was voided",
      sub: "This request was voided by the requester and can no longer be completed.",
      showDownload: false,
    };
  }

  if (status === strings.expired) {
    return {
      show: true,
      isError: true,
      title: "This request has expired",
      sub: "The signing window closed before all signers completed. Send a new request to continue.",
      showDownload: false,
    };
  }

  return hiddenBanner;
};

/** "CS" from "Christopher Slishinski" — at most two letters, as in the design. */
export const getRecipientInitials = (name?: string) =>
  (name ?? "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? "")
    .join("")
    .toUpperCase();

/** Security line under a recipient, worded as in the design. */
export const getSecurityLabel = (participant: RequestParticipant) => {
  if (participant.cc) return "No action needed";
  const security = participant.requestSecurity;
  if (security?.needOTPValidation) return "Email OTP";
  if (security?.securityCode?.length) return "Security code";
  return "No security";
};

export const formatRequestDateTime = (value?: string) =>
  value ? convertESTtoUserLocalDateAndTime(value, DATE_TIME_FORMAT, REQUEST_DATE_INPUT) : "";

/** Date without the clock time — the phone header has no room for both. */
export const formatRequestDate = (value?: string) =>
  value ? convertESTtoUserLocalDateAndTime(value, DATE_FORMAT, REQUEST_DATE_INPUT) : "";

export const formatAuditDateTime = (value?: string) =>
  value ? convertESTtoUserLocalDateAndTime(value, DATE_TIME_FORMAT, AUDIT_DATE_INPUT) : "";

export const formatAuditDate = (value?: string) =>
  value ? convertESTtoUserLocalDateAndTime(value, DATE_FORMAT, AUDIT_DATE_INPUT) : "";

/** Audit events that close out a recipient's part of the request. */
const TERMINAL_EVENTS = [
  strings.auditStatuses.signed,
  strings.auditStatuses.approved,
  strings.auditStatuses.declined,
  strings.auditStatuses.rejected,
  strings.auditStatuses.delegated,
];

/** Audit events that mean the recipient was (re)notified. */
const NOTIFIED_EVENTS = [
  strings.auditStatuses.sent,
  strings.auditStatuses.resent,
  strings.auditStatuses.sentForApproval,
];

/** Audit events an approver performs — used to attribute an event to a role
 * when the audit's own `requestRole` is missing. */
const REVIEWER_AUDIT_EVENTS = [
  strings.auditStatuses.approved,
  strings.auditStatuses.rejected,
  strings.auditStatuses.sentForApproval,
];

export type RecipientAuditRole = "reviewer" | "signer" | "cc";

/**
 * The same person can be both an approver and a signer (same email), so audit
 * lines must be attributed to a role, not just an email. Prefer the audit's own
 * `requestRole`; fall back to the event type when it's absent.
 */
export const canonicalRecipientRole = (
  role?: string,
  eventType?: string,
): RecipientAuditRole => {
  const r = (role ?? "").toLowerCase();
  if (r.includes("review") || r.includes("approv")) return "reviewer";
  if (r.includes("cc")) return "cc";
  if (r.includes("sign")) return "signer";
  const event = (eventType ?? "").toLowerCase();
  return REVIEWER_AUDIT_EVENTS.includes(event) ? "reviewer" : "signer";
};

/** Composite key for the per-recipient audit maps: role + email, so an
 * approver row and a signer row for the same person never share a line. */
export const recipientAuditKey = (
  role: RecipientAuditRole,
  email: string,
): string => `${role}::${email.toLowerCase()}`;

/**
 * Builds the "Signed Jul 10, 2026 · 2:41 AM" line the design shows beside each
 * recipient's security method. Everything comes from the audit trail the screen
 * already loads — a recipient with no matching event simply has no line.
 */
export const buildRecipientTimestamps = (audits: RequestAudit[] = []) => {
  // Keyed by role + email (see `recipientAuditKey`): the same person can be both
  // an approver and a signer, and each role's row needs its own line.
  const byKey = new Map<string, string>();

  const sorted = [...audits].sort((a, b) =>
    (a.createdOn ?? "").localeCompare(b.createdOn ?? ""),
  );

  sorted.forEach((audit) => {
    const email = audit.createdBy?.email;
    const event = audit.eventType?.toLowerCase();
    if (!email || !event) return;
    const key = recipientAuditKey(
      canonicalRecipientRole(audit.requestRole, event),
      email,
    );

    if (TERMINAL_EVENTS.includes(event)) {
      byKey.set(
        key,
        `${splitByCamelCase(event)} ${formatAuditDateTime(audit.createdOn)}`,
      );
      return;
    }
    // Only fills the gap while the recipient still has to act — a terminal
    // event later in the loop overwrites it.
    if (NOTIFIED_EVENTS.includes(event) && !byKey.has(key)) {
      byKey.set(key, `Notified ${formatAuditDateTime(audit.createdOn)}`);
    }
  });

  return byKey;
};

/**
 * Reason each recipient gave when declining, keyed by email — the design shows
 * it inline under the signer instead of hiding it behind the document viewer.
 */
export const buildDeclineReasons = (audits: RequestAudit[] = []) => {
  // Keyed by role + email, for the same reason as `buildRecipientTimestamps`.
  const byKey = new Map<string, string>();

  audits.forEach((audit) => {
    const email = audit.createdBy?.email;
    const event = audit.eventType?.toLowerCase();
    const reason = String(audit.reason ?? "").trim();
    if (!email || !reason) return;
    if (
      event === strings.auditStatuses.declined ||
      event === strings.auditStatuses.rejected
    ) {
      byKey.set(
        recipientAuditKey(
          canonicalRecipientRole(audit.requestRole, event),
          email,
        ),
        reason,
      );
    }
  });

  return byKey;
};

/** Formatted date of the event that completed the request, if there is one. */
export const getCompletedOn = (audits: RequestAudit[] = []) => {
  const completed = audits.find(
    (audit) =>
      audit.eventType?.toLowerCase() === strings.auditStatuses.completed,
  );
  return completed ? formatAuditDate(completed.createdOn) : "";
};
